import { Themes } from '@ankr.com/ui';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import {
  AppKitNetwork,
  arbitrum,
  arbitrumSepolia,
  avalancheFuji,
  bsc,
  bscTestnet,
  fantom,
  fantomTestnet,
  holesky,
  sepolia,
  mainnet,
  optimism,
  optimismSepolia,
  polygon,
  scroll,
  scrollSepolia,
} from '@reown/appkit/networks';
import { createAppKit } from '@reown/appkit/react';
import { http } from 'wagmi';

import { store } from 'store';

import { name } from '../../../../package.json';
import {
  ARBITRUM_RPC_URL,
  ARBITRUM_SEPOLIA_TESTNET_RPC_URL,
  AVALANCHE_FUJI_TESTNET_RPC_URL,
  BSC_RPC_URL,
  BSC_TESTNET_RPC_URL,
  ETH_HOLESKY_RPC_URL,
  ETH_SEPOLIA_RPC_URL,
  ETH_RPC_URL,
  FANTOM_RPC_URL,
  OPTIMISIM_RPC_URL,
  OPTIMISM_TESTNET_RPC_URL,
  POLYGON_RPC_URL,
  SCROLL_RPC_URL,
  SCROLL_SEPOLIA_TESTNET_RPC_URL,
} from '../constants';
import { addCustomRpcUrl } from './utils/addCustomRpcUrl';

const networks: [AppKitNetwork, ...AppKitNetwork[]] = [
  addCustomRpcUrl(mainnet, ETH_RPC_URL),
  addCustomRpcUrl(arbitrum, ARBITRUM_RPC_URL),
  addCustomRpcUrl(bsc, BSC_RPC_URL),
  addCustomRpcUrl(fantom, FANTOM_RPC_URL),
  addCustomRpcUrl(optimism, OPTIMISIM_RPC_URL),
  addCustomRpcUrl(polygon, POLYGON_RPC_URL),
  addCustomRpcUrl(scroll, SCROLL_RPC_URL),
  fantomTestnet, // fantom testnet is not supported with freemium key
  addCustomRpcUrl(arbitrumSepolia, ARBITRUM_SEPOLIA_TESTNET_RPC_URL),
  addCustomRpcUrl(avalancheFuji, AVALANCHE_FUJI_TESTNET_RPC_URL),
  addCustomRpcUrl(bscTestnet, BSC_TESTNET_RPC_URL),
  addCustomRpcUrl(holesky, ETH_HOLESKY_RPC_URL),
  addCustomRpcUrl(sepolia, ETH_SEPOLIA_RPC_URL),
  addCustomRpcUrl(optimismSepolia, OPTIMISM_TESTNET_RPC_URL),
  addCustomRpcUrl(scrollSepolia, SCROLL_SEPOLIA_TESTNET_RPC_URL),
];

const transports = {
  [mainnet.id]: http(ETH_RPC_URL),
  [arbitrum.id]: http(ARBITRUM_RPC_URL),
  [bsc.id]: http(BSC_RPC_URL),
  [fantom.id]: http(FANTOM_RPC_URL),
  [optimism.id]: http(OPTIMISIM_RPC_URL),
  [polygon.id]: http(POLYGON_RPC_URL),
  [scroll.id]: http(SCROLL_RPC_URL),
  [arbitrumSepolia.id]: http(ARBITRUM_SEPOLIA_TESTNET_RPC_URL),
  [avalancheFuji.id]: http(AVALANCHE_FUJI_TESTNET_RPC_URL),
  [bscTestnet.id]: http(BSC_TESTNET_RPC_URL),
  [holesky.id]: http(ETH_HOLESKY_RPC_URL),
  [sepolia.id]: http(ETH_SEPOLIA_RPC_URL),
  [optimismSepolia.id]: http(OPTIMISM_TESTNET_RPC_URL),
  [scrollSepolia.id]: http(SCROLL_SEPOLIA_TESTNET_RPC_URL),
};

const customRpcUrls = {
  'eip155:1': [{ url: ETH_RPC_URL }],
  'eip155:137': [{ url: POLYGON_RPC_URL }],
  'eip155:42161': [{ url: ARBITRUM_RPC_URL }],
  'eip155:56': [{ url: BSC_RPC_URL }],
  'eip155:250': [{ url: FANTOM_RPC_URL }],
  'eip155:10': [{ url: OPTIMISIM_RPC_URL }],
  'eip155:534352': [{ url: SCROLL_RPC_URL }],
  'eip155:421614': [{ url: ARBITRUM_SEPOLIA_TESTNET_RPC_URL }],
  'eip155:97': [{ url: BSC_TESTNET_RPC_URL }],
  'eip155:17000': [{ url: ETH_HOLESKY_RPC_URL }],
  'eip155:11155111': [{ url: ETH_SEPOLIA_RPC_URL }],
  'eip155:11155420': [{ url: OPTIMISM_TESTNET_RPC_URL }],
  'eip155:534351': [{ url: SCROLL_SEPOLIA_TESTNET_RPC_URL }],
};

const projectId = '8632f6ad3de177c2e04ba5248e27a743';

const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId,
  transports,
  customRpcUrls,
  ssr: true,
});

export const { wagmiConfig } = wagmiAdapter;

const getAppKitThemeMode = (): 'light' | 'dark' => {
  const theme = store.getState().theme?.theme;

  return theme === Themes.dark ? 'dark' : 'light';
};

let currentThemeMode = getAppKitThemeMode();

const appKit = createAppKit({
  adapters: [wagmiAdapter],
  themeMode: currentThemeMode,
  networks,
  projectId,
  metadata: {
    name,
    description: 'Web3 API application',
    url: 'https://www.ankr.com/rpc',
    icons: ['https://avatars.githubusercontent.com/u/35610494'],
  },
  features: {
    analytics: false,
    email: false,
    socials: false,
    onramp: false,
    swaps: false,
    legalCheckbox: false,
  },
  themeVariables: {
    '--w3m-z-index': 1301,
  },
  allWallets: 'HIDE',
  enableWalletGuide: false,
  allowUnsupportedChain: true,
  customRpcUrls,
});

store.subscribe(() => {
  const nextThemeMode = getAppKitThemeMode();

  if (nextThemeMode !== currentThemeMode) {
    currentThemeMode = nextThemeMode;
    appKit.setThemeMode(nextThemeMode);
  }
});
